import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import "@/App.css";
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import './theme.css';
import { useSubscription } from './utils/useSubscription';
import { SubscriptionWall, TrialBanner } from './components/SubscriptionWall';
import { Auth } from './components/Auth';
import { LoginHub } from './components/LoginHub';
import AdminLMSCourses from './components/AdminLMSCourses';
import AdminCourseEditor from './components/AdminCourseEditor';
import LMSDashboard from './components/LMSDashboard';
import CourseViewer from './components/CourseViewer';
import ExamInterface from './components/ExamInterface';
import './registerServiceWorker';  // Register PWA Service Worker
import Dashboard from './components/Dashboard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth Callback component for Google OAuth
function AuthCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        
        try {
          const response = await fetch(`${API_URL}/api/auth/session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: sessionId })
          });
          
          if (response.ok) {
            const userData = await response.json();
            login(userData, userData.token);
            // navigate replaces the hash route, discarding the session_id fragment
            navigate('/hub', { state: { user: userData }, replace: true });
          } else {
            console.error('Auth callback failed');
            navigate('/login', { replace: true });
          }
        } catch (error) {
          console.error('Auth callback error:', error);
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );
}

function ProtectedRoute({ children, requireSubscription = false }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const subscriptionStatus = useSubscription(user);

  useEffect(() => {
    // If user passed from AuthCallback, skip auth check
    if (location.state?.user) return;
    
    if (!loading && !user) {
      navigate('/login', { replace: true });
    }
  }, [user, loading, navigate, location.state]);

  if (loading || subscriptionStatus.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Allow rendering if user from state or context
  if (location.state?.user || user) {
    // Subscription wall: only for the App Móvil (dashboard). Blocks usage after the 7-day trial.
    if (requireSubscription && !subscriptionStatus.hasAccess) {
      return <SubscriptionWall userEmail={(location.state?.user || user)?.email} />;
    }

    return (
      <>
        {requireSubscription && subscriptionStatus.status === 'trial' && subscriptionStatus.daysLeft <= 2 && (
          <TrialBanner daysLeft={subscriptionStatus.daysLeft} />
        )}
        {children}
      </>
    );
  }

  return null;
}

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/login" element={<Auth />} />
      <Route path="/hub" element={
        <ProtectedRoute>
          <LoginHub />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute requireSubscription={true}>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      {/* LMS Routes - Student */}
      <Route path="/lms" element={
        <ProtectedRoute>
          <LMSDashboard />
        </ProtectedRoute>
      } />
      <Route path="/lms/course/:courseId" element={
        <ProtectedRoute>
          <CourseViewer />
        </ProtectedRoute>
      } />
      <Route path="/lms/exam/:examId" element={
        <ProtectedRoute>
          <ExamInterface />
        </ProtectedRoute>
      } />
      
      {/* LMS Routes - Admin */}
      <Route path="/admin/lms" element={
        <ProtectedRoute>
          <AdminLMSCourses />
        </ProtectedRoute>
      } />
      <Route path="/admin/lms/courses/:courseId" element={
        <ProtectedRoute>
          <AdminCourseEditor />
        </ProtectedRoute>
      } />
      
      <Route path="*" element={
        <ProtectedRoute>
          <LoginHub />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <ThemeProvider>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </ThemeProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
